import type { ReactNode } from 'react';
import { Box, Paper, Typography, alpha } from '@mui/material';

interface PageHeaderBannerProps {
    icon: ReactNode;
    title: ReactNode;
    subtitle: ReactNode;
    actions?: ReactNode;
    accentColor?: string;
}

/** Professional accent-gradient page header banner shared across MUI pages. */
const PageHeaderBanner = ({ icon, title, subtitle, actions, accentColor }: PageHeaderBannerProps) => (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            background: theme =>
                `linear-gradient(135deg, ${alpha(
                    accentColor ?? theme.palette.primary.main,
                    0.14,
                )}, ${alpha(accentColor ?? theme.palette.primary.main, 0.03)})`,
            border: theme =>
                `1px solid ${alpha(accentColor ?? theme.palette.primary.main, 0.18)}`,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    flexShrink: 0,
                    color: '#fff',
                    backgroundColor: theme => accentColor ?? theme.palette.primary.main,
                    boxShadow: theme =>
                        `0 6px 16px ${alpha(accentColor ?? theme.palette.primary.main, 0.4)}`,
                    '& svg': { fontSize: 28 },
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {subtitle}
                </Typography>
            </Box>
        </Box>
        {actions && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {actions}
            </Box>
        )}
    </Paper>
);

export default PageHeaderBanner;
